import { useState, useCallback } from 'react'
import { ExpertReview, ReviewResponses } from '../types'
import { supabase } from '../supabaseClient'

export function useExpertReviews(experimentId: string | undefined) {
  const [reviews, setReviews] = useState<ExpertReview[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!experimentId) {
      setReviews([])
      return
    }
    setLoading(true)
    const { data, error } = await supabase
      .from('expert_reviews')
      .select('*')
      .eq('experiment_id', experimentId)
      .order('created_at', { ascending: false })

    if (error) console.error('useExpertReviews load error:', error)
    setReviews((data ?? []) as ExpertReview[])
    setLoading(false)
  }, [experimentId])

  // Merge-patch the responses blob for a single (ku, reviewer) review row.
  // Refuses to write if the row is already locked (defense in depth; DB RLS
  // also enforces this via the RESTRICTIVE policy in 20260417000000).
  //
  // Optimistic: updates local state immediately so clicking a Likert button
  // doesn't trigger a full reload + re-render flash. DB write happens in the
  // background; on success we swap in the server-returned row (with its real
  // id). On error we log but don't revert — the next full load resyncs.
  const patchResponses = useCallback(
    async (kuId: string, reviewerId: string, patch: Partial<ReviewResponses>) => {
      if (!experimentId) return
      const nowIso = new Date().toISOString()
      const existing = reviews.find(
        (r) => r.ku_id === kuId && r.reviewer_id === reviewerId && r.experiment_id === experimentId,
      )
      if (existing?.locked_at) {
        console.warn('patchResponses: review is locked; ignoring write', kuId)
        return
      }
      const merged: ReviewResponses = { ...(existing?.responses ?? {}), ...patch }

      // 1. Optimistic local update — no reload, no flash.
      setReviews((prev) => {
        const idx = prev.findIndex(
          (r) => r.ku_id === kuId && r.reviewer_id === reviewerId && r.experiment_id === experimentId,
        )
        if (idx >= 0) {
          const updated = [...prev]
          updated[idx] = { ...updated[idx], responses: merged, updated_at: nowIso }
          return updated
        }
        // First save for this KU — insert a placeholder row with a temp id.
        return [
          ...prev,
          {
            id: `temp-${kuId}`,
            ku_id: kuId,
            experiment_id: experimentId,
            reviewer_id: reviewerId,
            responses: merged,
            locked_at: null,
            created_at: nowIso,
            updated_at: nowIso,
          } as ExpertReview,
        ]
      })

      // 2. Background DB write — upsert and replace the placeholder id on success.
      const { data, error } = await supabase
        .from('expert_reviews')
        .upsert(
          {
            ku_id: kuId,
            experiment_id: experimentId,
            reviewer_id: reviewerId,
            responses: merged,
            updated_at: nowIso,
          },
          { onConflict: 'ku_id,experiment_id,reviewer_id' },
        )
        .select()
        .single()
      if (error) {
        console.error('patchResponses error:', error)
        return
      }
      if (data) {
        setReviews((prev) =>
          prev.map((r) =>
            r.ku_id === kuId && r.reviewer_id === reviewerId && r.experiment_id === experimentId
              ? (data as ExpertReview)
              : r,
          ),
        )
      }
    },
    [experimentId, reviews],
  )

  const saveStep1 = useCallback(
    async (kuId: string, reviewerId: string, step1: Partial<ReviewResponses>) => {
      await patchResponses(kuId, reviewerId, { ...step1, step1_saved_at: new Date().toISOString() })
    },
    [patchResponses],
  )

  const saveStep2 = useCallback(
    async (kuId: string, reviewerId: string, step2: Partial<ReviewResponses>) => {
      await patchResponses(kuId, reviewerId, { ...step2, step2_saved_at: new Date().toISOString() })
    },
    [patchResponses],
  )

  // Lock a single per-KU review. Once locked, no more writes to the
  // responses blob for that row are permitted (RLS enforces).
  const lockReview = useCallback(
    async (kuId: string, reviewerId: string) => {
      if (!experimentId) return
      const existing = reviews.find(
        (r) => r.ku_id === kuId && r.reviewer_id === reviewerId && r.experiment_id === experimentId,
      )
      if (!existing) {
        console.warn('lockReview: no row to lock', kuId)
        return
      }
      const { error } = await supabase
        .from('expert_reviews')
        .update({ locked_at: new Date().toISOString() })
        .eq('id', existing.id)
      if (error) console.error('lockReview error:', error)
      await load()
    },
    [experimentId, reviews, load],
  )

  const getReviewForKU = useCallback(
    (kuId: string, reviewerId?: string): ExpertReview | undefined => {
      return reviews.find((r) => r.ku_id === kuId && (!reviewerId || r.reviewer_id === reviewerId))
    },
    [reviews],
  )

  const getReviewedKUIds = useCallback((): Set<string> => {
    return new Set(reviews.filter((r) => r.locked_at).map((r) => r.ku_id))
  }, [reviews])

  return {
    reviews,
    loading,
    load,
    saveStep1,
    saveStep2,
    lockReview,
    getReviewForKU,
    getReviewedKUIds,
  }
}

// ── Assigned-experiments hook (for the expert's landing page) ────────────────
// Uses the experiment_reviewers junction so experts see only experiments
// they're invited to. Researchers see their own + any they're also assigned
// to review.
export function useAssignedExperiments(userId: string) {
  const [rows, setRows] = useState<{ experiment_id: string; name: string; invited_at: string }[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!userId) {
      setRows([])
      setLoading(false)
      return
    }
    setLoading(true)
    const { data, error } = await supabase
      .from('experiment_reviewers')
      .select('experiment_id, invited_at, experiments(name)')
      .eq('reviewer_id', userId)
      .order('invited_at', { ascending: false })

    if (error) console.error('useAssignedExperiments load error:', error)
    const mapped = (data ?? []).map((r) => {
      // supabase-js infers embedded relations as arrays even when the FK
      // returns a single row at runtime — handle both shapes defensively.
      const exp = Array.isArray(r.experiments) ? r.experiments[0] : r.experiments
      return {
        experiment_id: r.experiment_id,
        name: exp?.name ?? '(unknown)',
        invited_at: r.invited_at,
      }
    })
    setRows(mapped)
    setLoading(false)
  }, [userId])

  return { rows, loading, load }
}
